export type PromotionalDetailsConfig = {
  enabled: boolean;
  mode?: 'auto' | 'manual' | 'creator';
  linkRequired?: boolean;
  landingPageUrl?: string;
  instructions: string;
  codeRequired: boolean;
};

export type CreatorPromotionalSettings = {
  promotionalLinkRequired?: boolean | null;
  landingpageurlMode?: string | null;
  promotionalLinkInstructions?: string | null;
  promotionalCodeRequired?: boolean | null;
  promotionalDetailsEnabled?: boolean;
  promotionalDetails?: unknown;
};

export type CreatorPromotionalStatus =
  | 'not_submitted'
  | 'pending_review'
  | 'changes_requested'
  | 'confirmed';
export type CreatorPromotionalHistoryStatus =
  | CreatorPromotionalStatus
  | 'previous_version';
export type CreatorPromotionalVersion = {
  version: number;
  status?: CreatorPromotionalHistoryStatus;
  submittedBy?: 'CREATOR' | 'ADVERTISER';
  link: string;
  code?: string;
  note?: string;
  submittedAt?: string;
  reviewedAt?: string;
};
/** UI projection. revision is concurrency control; version labels a submitted version. */
export type CreatorPromotionalDetails = CreatorPromotionalVersion & {
  status: CreatorPromotionalStatus;
  revision: number;
  config?: PromotionalDetailsConfig;
  canSubmit?: boolean;
  canReview?: boolean;
  history?: CreatorPromotionalVersion[];
};
export type CreatorPromotionalSubmission = {
  expectedRevision: number;
  link: string;
  code?: string;
};
export type CreatorPromotionalReview = {
  expectedRevision: number;
  action: 'confirm' | 'request_changes' | 'replace';
  link?: string;
  code?: string;
  note?: string;
  creatorTranslation?: {
    sourceLanguage: string;
    targetLanguage: string;
    translatedContent: string;
  };
};

const record = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const revisionValid = (value: unknown): value is number =>
  Number.isSafeInteger(value) && (value as number) >= 0;
const optionalString = (value: unknown) =>
  value === undefined || typeof value === 'string';
const optionalDate = (value: unknown) =>
  value === undefined ||
  (typeof value === 'string' && Number.isFinite(Date.parse(value)));

function readConfig(value: unknown): PromotionalDetailsConfig | null {
  if (
    !record(value) ||
    typeof value.enabled !== 'boolean' ||
    typeof value.instructions !== 'string' ||
    typeof value.codeRequired !== 'boolean' ||
    (value.mode !== undefined &&
      !['auto', 'manual', 'creator'].includes(value.mode)) ||
    (value.linkRequired !== undefined &&
      typeof value.linkRequired !== 'boolean') ||
    (value.landingPageUrl !== null && !optionalString(value.landingPageUrl))
  )
    return null;
  return {
    enabled: value.enabled,
    mode: value.mode,
    linkRequired: value.linkRequired,
    landingPageUrl: value.landingPageUrl ?? undefined,
    instructions: value.instructions,
    codeRequired: value.codeRequired
  };
}

export function getCreatorPromotionalSettings(
  order?: CreatorPromotionalSettings | null,
  campaign?: CreatorPromotionalSettings | null
): CreatorPromotionalSettings {
  const details = order?.promotionalDetails;
  if (record(details) && record(details.config)) {
    const config = details.config;
    // The order's config includes frozen configuration; never overwrite it with campaign values.
    return {
      promotionalDetailsEnabled: config.enabled === true,
      landingpageurlMode:
        config.mode ?? (config.enabled === true ? 'creator' : undefined),
      promotionalLinkRequired: config.linkRequired !== false,
      promotionalLinkInstructions:
        typeof config.instructions === 'string' ? config.instructions : '',
      promotionalCodeRequired: config.codeRequired !== false
    };
  }
  return {
    promotionalLinkRequired:
      order?.promotionalLinkRequired ??
      campaign?.promotionalLinkRequired ??
      true,
    landingpageurlMode: order?.landingpageurlMode,
    promotionalLinkInstructions: order?.promotionalLinkInstructions,
    promotionalCodeRequired: order?.promotionalCodeRequired ?? true
  };
}
export function isCreatorPromotionalMode(
  settings?: CreatorPromotionalSettings | null
) {
  return (
    settings?.promotionalDetailsEnabled !== false &&
    settings?.promotionalLinkRequired !== false &&
    settings?.landingpageurlMode === 'creator'
  );
}
export function isValidCreatorPromotionalLink(value: string) {
  if (!/^https?:\/\//i.test(value) || /\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return (
      ['https:', 'http:'].includes(url.protocol) &&
      !!url.hostname &&
      !url.username &&
      !url.password
    );
  } catch {
    return false;
  }
}

const statusMap: Record<string, CreatorPromotionalStatus> = {
  PENDING_REVIEW: 'pending_review',
  CONFIRMED: 'confirmed',
  CHANGES_REQUESTED: 'changes_requested'
};
const historyStatusMap: Record<string, CreatorPromotionalHistoryStatus> = {
  PENDING_REVIEW: 'pending_review',
  CONFIRMED: 'confirmed',
  CHANGES_REQUESTED: 'changes_requested',
  PREVIOUS_VERSION: 'previous_version'
};
function submissionFields(value: unknown): CreatorPromotionalVersion | null {
  if (
    !record(value) ||
    !revisionValid(value.version) ||
    value.version < 1 ||
    typeof value.link !== 'string' ||
    (value.code !== null && !optionalString(value.code)) ||
    (value.feedback !== null && !optionalString(value.feedback)) ||
    !['CREATOR', 'ADVERTISER'].includes(value.submittedBy) ||
    typeof value.submittedAt !== 'string' ||
    !optionalDate(value.submittedAt)
  )
    return null;
  const status =
    typeof value.status === 'string' &&
    Object.prototype.hasOwnProperty.call(historyStatusMap, value.status)
      ? historyStatusMap[value.status]
      : undefined;
  return {
    ...(status ? { status } : {}),
    version: value.version,
    link: value.link,
    code: value.code ?? undefined,
    submittedBy: value.submittedBy,
    note: value.feedback ?? undefined,
    submittedAt: value.submittedAt
  };
}
/** Converts the documented GET/POST PromotionalDetailsView, including the empty view. */
export function normalizePromotionalDetailsView(
  value: unknown
): CreatorPromotionalDetails | null {
  if (!record(value)) return null;
  const config = readConfig(value.config);
  if (
    !config ||
    !revisionValid(value.revision) ||
    typeof value.canSubmit !== 'boolean' ||
    typeof value.canReview !== 'boolean'
  )
    return null;
  const common = {
    config,
    revision: value.revision,
    canSubmit: value.canSubmit,
    canReview: value.canReview
  };
  if (value.submission === null)
    return {
      ...common,
      version: 0,
      status: 'not_submitted',
      link: '',
      history: []
    };
  const current = submissionFields(value.submission);
  const submission = value.submission;
  if (
    !current ||
    !record(submission) ||
    typeof submission.status !== 'string' ||
    !Object.prototype.hasOwnProperty.call(statusMap, submission.status) ||
    submission.revision !== value.revision ||
    (submission.reviewedAt !== null && !optionalDate(submission.reviewedAt)) ||
    !Array.isArray(submission.history)
  )
    return null;
  const history = submission.history.map(submissionFields);
  if (history.some((entry: CreatorPromotionalVersion | null) => !entry))
    return null;
  return {
    ...common,
    ...current,
    status: statusMap[submission.status],
    reviewedAt: submission.reviewedAt ?? undefined,
    history: history as CreatorPromotionalVersion[]
  };
}
/** Reads the wire view or an already-normalized local UI receipt. */
export function readCreatorPromotionalDetails(
  value: unknown
): CreatorPromotionalDetails | null {
  if (!record(value)) return null;
  if ('submission' in value) return normalizePromotionalDetailsView(value);
  if (
    !revisionValid(value.version) ||
    ![
      'not_submitted',
      'pending_review',
      'changes_requested',
      'confirmed'
    ].includes(value.status) ||
    typeof value.link !== 'string' ||
    !optionalString(value.code) ||
    !optionalString(value.note) ||
    !optionalDate(value.submittedAt) ||
    !optionalDate(value.reviewedAt) ||
    !revisionValid(value.revision) ||
    (value.history !== undefined && !Array.isArray(value.history))
  )
    return null;
  return value as CreatorPromotionalDetails;
}
export function getCreatorPromotionalRevision(
  value: CreatorPromotionalDetails | null | undefined
) {
  return value?.revision ?? 0;
}
export function hasConfirmedCreatorPromotionalDetails(
  settings: CreatorPromotionalSettings,
  value: unknown
) {
  if (!isCreatorPromotionalMode(settings)) return true;
  const details = readCreatorPromotionalDetails(value);
  return (
    !!details &&
    details.version > 0 &&
    ['confirmed'].includes(details.status) &&
    isValidCreatorPromotionalLink(details.link) &&
    (settings.promotionalCodeRequired === false || !!details.code?.trim())
  );
}
export function hasSubmittedCreatorPromotionalDetails(
  settings: CreatorPromotionalSettings,
  value: unknown
) {
  if (!isCreatorPromotionalMode(settings)) return true;
  const details = readCreatorPromotionalDetails(value);
  return (
    !!details &&
    details.version > 0 &&
    ['pending_review', 'confirmed'].includes(details.status) &&
    isValidCreatorPromotionalLink(details.link) &&
    (settings.promotionalCodeRequired === false || !!details.code?.trim())
  );
}

export class PromotionalDetailsApiError extends Error {
  code?: number;
  status?: number;
  reason?: string;
  payload?: Record<string, unknown>;
  constructor(error: unknown) {
    const input = record(error) ? error : {};
    const body = input.response?.data ?? input.responseData ?? input;
    const payload = record(body.payload) ? body.payload : undefined;
    const message =
      typeof body.message === 'string'
        ? body.message
        : typeof body.error === 'string'
          ? body.error
          : error instanceof Error
            ? error.message
            : 'Request failed.';
    super(message);
    this.name = 'PromotionalDetailsApiError';
    this.code = typeof body.code === 'number' ? body.code : undefined;
    this.status = input.response?.status ?? input.status;
    this.payload = payload;
    this.reason =
      typeof payload?.reason === 'string' ? payload.reason : message;
  }
}
export function isPromotionalDetailsConflict(error: unknown) {
  const parsed =
    error instanceof PromotionalDetailsApiError
      ? error
      : new PromotionalDetailsApiError(error);
  return (
    parsed.code === 40009 ||
    parsed.status === 409 ||
    parsed.reason === 'UPDATE_CONFLICT'
  );
}
export function getPromotionalDetailsGate(error: unknown) {
  const parsed =
    error instanceof PromotionalDetailsApiError
      ? error
      : new PromotionalDetailsApiError(error);
  return parsed.reason === 'PROMOTIONAL_DETAILS_REQUIRED' ||
    parsed.reason === 'PROMOTIONAL_DETAILS_CONFIRMATION_REQUIRED'
    ? parsed.reason
    : null;
}

export function getCreatorOrderPromotionalLink(
  order?: CreatorPromotionalSettings & {
    utmLink?: string;
    promotionalDetails?: unknown;
  },
  campaign?: CreatorPromotionalSettings | null
) {
  if (!isCreatorPromotionalMode(getCreatorPromotionalSettings(order, campaign)))
    return order?.utmLink;
  const details = readCreatorPromotionalDetails(order?.promotionalDetails);
  return details &&
    ['pending_review', 'confirmed'].includes(details.status) &&
    isValidCreatorPromotionalLink(details.link)
    ? details.link
    : undefined;
}
